import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleSlash, Play } from "lucide-react";
import {
  AppLink,
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  GridWrap,
  LinkButton,
  PopoverHeading,
  ReadOnlyNote,
  ReasonField,
  SectionHeader,
  SegmentedFilter,
  SkeletonRows,
  Status,
  StatusPopover,
  Toolbar,
  ToolbarSpacer,
  dataColumns,
  formatDate,
  formatDateTime,
  useReason,
  useToast,
  type StatusTone,
  errorText,
  useCommandMutation,
} from "@amber/ui";
import { DependenciesPanel } from "@amber/operations/ui";
import type { RouteView } from "../contracts/index";
import {
  quarantinePreviewQuery,
  releasePreviewQuery,
  routesQuery,
  AFTER_COLLECTION_CHANGE,
  ON_COLLECTION_CONFLICT,
  routeCommands,
} from "./queries";
import { RouteQualification } from "./RouteQualification";

const STATE_TONE: Record<RouteView["state"], StatusTone> = {
  active: "ok",
  cooling: "waiting",
  quarantined: "attention",
  releasing: "running",
  stopped: "attention",
  not_ready: "off",
  not_configured: "off",
};

function RouteState({ route }: { route: RouteView }) {
  const status = <Status tone={STATE_TONE[route.state]}>{route.state_label}</Status>;
  if (!route.state_detail) return status;
  const d = route.state_detail;
  return (
    <StatusPopover label={status} accessibleLabel={`${route.state_label}: details`}>
      <PopoverHeading>{route.state_label}</PopoverHeading>
      {d.reason ? <p className="m-0 mb-1.5">{d.reason}</p> : null}
      <dl className="m-0 grid grid-cols-[64px_1fr] gap-x-2 gap-y-0.5 text-md [&_dd]:m-0 [&_dt]:text-muted">
        <dt>Since</dt>
        <dd>{formatDateTime(d.since)}</dd>
        {d.by ? (
          <>
            <dt>By</dt>
            <dd>{d.by}</dd>
          </>
        ) : null}
        {d.until ? (
          <>
            <dt>Until</dt>
            <dd>{formatDateTime(d.until)} · ends automatically</dd>
          </>
        ) : (
          <>
            <dt>Ends</dt>
            <dd>{route.state === "cooling" ? "Automatically" : "No automatic end"}</dd>
          </>
        )}
      </dl>
    </StatusPopover>
  );
}

type RouteFilter = "all" | "acquisition" | "processing";

function megabytes(bytes: number): string {
  if (bytes === 0) return "0 MB";
  const mb = bytes / 1_000_000;
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${String(Math.round(mb))} MB`;
}

function seconds(ms: number | null): string | null {
  if (ms === null) return null;
  return ms < 1000 ? `${String(ms)} ms` : `${String(Math.round(ms / 100) / 10)} s`;
}

function QuarantineDialog({ route, onClose }: { route: RouteView; onClose: () => void }) {
  const toast = useToast();
  const preview = useQuery(quarantinePreviewQuery(route.id));
  const r = useReason();
  const mutation = useCommandMutation({
    run: (csrf) => routeCommands.quarantine(route.id, { base_revision: route.revision ?? 1, reason: r.trimmed }, csrf),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: () => {
      toast.show(`${route.name} is quarantined`);
      onClose();
    },
  });
  const p = preview.data;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Quarantine ${route.name}?`}
      onSubmit={() => {
        if (r.submit()) mutation.mutate();
      }}
      footer={<DialogFooter onClose={onClose} confirm="Quarantine" pending={mutation.isPending} />}
    >
      <DialogText>
        The route is skipped from now on until someone releases it. Attempts already running finish.
      </DialogText>
      {p ? (
        <dl className="m-0 mb-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-divider bg-surface px-3 py-2.5 text-md [&_dd]:m-0 [&_dt]:text-muted">
          <dt>Queued work</dt>
          <dd>
            {p.queued === 1
              ? "1 item would use this route next"
              : `${String(p.queued)} items would use this route next`}
          </dd>
          <dt>Moves to</dt>
          <dd>{p.moves_to ?? `Nothing: work waits in front of ${p.stage}`}</dd>
        </dl>
      ) : (
        <SkeletonRows columns={1} rows={2} />
      )}
      {p?.suggestion ? (
        <Callout tone="waiting" className="mb-2">
          {p.suggestion}
        </Callout>
      ) : null}
      <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}

function ReleaseDialog({ route, onClose }: { route: RouteView; onClose: () => void }) {
  const toast = useToast();
  const preview = useQuery(releasePreviewQuery(route.id));
  const r = useReason();
  const [failed, setFailed] = useState<string | null>(null);
  const mutation = useCommandMutation({
    run: (csrf) => routeCommands.release(route.id, { base_revision: route.revision ?? 1, reason: r.trimmed }, csrf),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: (result) => {
      if (result.outcome === "test_failed") {
        setFailed(result.detail);
        return;
      }
      toast.show(
        result.resumed > 0
          ? `${route.name} is released · ${String(result.resumed)} held ${result.resumed === 1 ? "item returns" : "items return"}`
          : `${route.name} is released`,
      );
      onClose();
    },
  });
  const p = preview.data;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Release ${route.name}?`}
      onSubmit={() => {
        const ok = r.submit();
        setFailed(null);
        if (ok) mutation.mutate();
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          cancel={failed ? "Close" : "Cancel"}
          confirm={failed ? "Test again" : "Test and release"}
          pending={mutation.isPending}
        />
      }
    >
      {p ? (
        <dl className="m-0 mb-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-divider bg-surface px-3 py-2.5 text-md [&_dd]:m-0 [&_dt]:text-muted">
          <dt>Quarantined</dt>
          <dd>
            {formatDateTime(p.quarantined.since)}
            {p.quarantined.by ? ` by ${p.quarantined.by}` : ""}
            {p.quarantined.reason ? `: “${p.quarantined.reason}”` : ""}
          </dd>
          <dt>Held work</dt>
          <dd>{p.held === 1 ? "1 item waits for this route" : `${String(p.held)} items wait for this route`}</dd>
          <dt>First</dt>
          <dd>{p.test}</dd>
        </dl>
      ) : (
        <SkeletonRows columns={1} rows={3} />
      )}
      {failed ? (
        <Callout
          tone="attention"
          role="alert"
          className="mb-2"
          title="The test request failed; the route stays quarantined"
        >
          {failed}
        </Callout>
      ) : null}
      <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}

const col = dataColumns<RouteView>();

function CredentialDates({ credential: c }: { credential: NonNullable<RouteView["credential"]> }) {
  const verified = c.last_verified_at ? formatDateTime(c.last_verified_at) : null;
  if (!c.expires_at)
    return (
      <span className="block truncate text-md">{`${verified ? `Verified ${verified}` : "Not verified yet"} · no expiry`}</span>
    );
  return (
    <span className="block truncate text-md">
      <span className={c.expiring_soon ? "font-strong text-waiting" : undefined}>
        {`Expires ${formatDate(c.expires_at)}`}
      </span>
      {` · ${verified ? `verified ${verified}` : "not verified yet"}`}
    </span>
  );
}

function RouteActions({
  r,
  onQuarantine,
  onRelease,
}: {
  r: RouteView;
  onQuarantine: (route: RouteView) => void;
  onRelease: (route: RouteView) => void;
}) {
  if (!r.actions) return null;
  return r.state === "quarantined" || r.state === "releasing" ? (
    <Button
      size="sm"
      disabledReason={r.actions.release.enabled ? null : r.actions.release.reason}
      onClick={() => {
        onRelease(r);
      }}
    >
      <Play aria-hidden="true" />
      Release…
    </Button>
  ) : (
    <Button
      size="sm"
      disabledReason={r.actions.quarantine.enabled ? null : r.actions.quarantine.reason}
      onClick={() => {
        onQuarantine(r);
      }}
    >
      <CircleSlash aria-hidden="true" />
      Quarantine…
    </Button>
  );
}

function attemptsText(r: RouteView): string {
  const median = seconds(r.attempts_7d.median_ms);
  return (
    [
      r.attempts_7d.challenged > 0 ? `${String(r.attempts_7d.challenged)} challenged` : null,
      r.attempts_7d.total > 0 ? megabytes(r.attempts_7d.bytes) : null,
      median ? `median ${median}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "No attempts"
  );
}

function routeColumns(handlers: { onQuarantine: (route: RouteView) => void; onRelease: (route: RouteView) => void }) {
  return [
    col.display({
      id: "route",
      header: "Route",
      meta: { width: 220, cellClassName: "h-12" },
      cell: ({ row: { original: r } }) => (
        <>
          <span className="block truncate font-strong text-fg">{r.name}</span>
          <span className="block truncate text-md text-muted" title={r.use}>
            {r.use}
          </span>
        </>
      ),
    }),
    col.display({ id: "kind", header: "Kind", meta: { width: 124 }, cell: ({ row }) => row.original.kind }),
    col.display({
      id: "state",
      header: "State",
      meta: { width: 140, cellClassName: "overflow-visible" },
      cell: ({ row }) => <RouteState route={row.original} />,
    }),
    col.display({
      id: "qualification",
      header: "Qualification",
      meta: { width: 128, cellClassName: "overflow-visible" },
      cell: ({ row: { original: r } }) => <RouteQualification name={r.name} q={r.qualification} />,
    }),
    col.display({
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { width: 152, label: "Actions", cellClassName: "overflow-visible" },
      cell: ({ row }) => <RouteActions r={row.original} {...handlers} />,
    }),
    col.display({
      id: "credential",
      header: "Credential",
      meta: { width: 180 },
      cell: ({ row: { original: r } }) =>
        r.credential ? (
          <>
            <span className="block truncate font-mono text-md text-fg">{r.credential.name}</span>
            <CredentialDates credential={r.credential} />
          </>
        ) : (
          "None needed"
        ),
    }),
    col.display({ id: "quota", header: "Quota", meta: { width: 170 }, cell: ({ row }) => row.original.quota ?? "—" }),
    col.display({
      id: "attempts",
      header: "Last 7 days",
      meta: { width: 150, cellClassName: "text-md" },
      cell: ({ row: { original: r } }) =>
        r.id === "proxy_pool" || r.category === "processing" ? (
          "—"
        ) : (
          <>
            <span className="block font-mono text-fg">{`${String(r.attempts_7d.succeeded)} of ${String(r.attempts_7d.total)} ok`}</span>
            <span className="block truncate">{attemptsText(r)}</span>
          </>
        ),
    }),
    col.display({
      id: "last_failure",
      header: "Last failure",
      cell: ({ row: { original: r } }) =>
        r.last_failure ? (
          <>
            {formatDateTime(r.last_failure.at)} · {r.last_failure.outcome.replace(/_/g, " ")}
            <span className="block truncate text-md">{r.last_failure.detail}</span>
          </>
        ) : (
          <span className="text-muted">None</span>
        ),
    }),
  ];
}

const PINNED = ["route"];

export function RoutesSettings() {
  const query = useQuery(routesQuery);
  const [filter, setFilter] = useState<RouteFilter>("all");
  const routes = query.data?.routes ?? [];
  const rows = routes.filter((r) => filter === "all" || r.category === filter);
  const held = routes.filter((r) => r.state === "quarantined" || r.state === "stopped").length;
  const [quarantining, setQuarantining] = useState<RouteView | null>(null);
  const [releasing, setReleasing] = useState<RouteView | null>(null);
  const columns = useMemo(() => routeColumns({ onQuarantine: setQuarantining, onRelease: setReleasing }), []);
  const roleNote = routes.find(
    (r) =>
      r.actions &&
      !r.actions.quarantine.enabled &&
      r.actions.quarantine.required_role === "operator" &&
      r.actions.quarantine.reason.startsWith("Only"),
  );
  return (
    <section aria-labelledby="section-title" className="flex min-h-0 flex-1 flex-col overflow-auto">
      <SectionHeader
        titleId="section-title"
        title="Routes"
        lead="The ways we acquire and process material. A quarantined route is skipped; work moves to the next route or waits."
        meta={
          <>
            <AppLink href="/settings/audit?subject=route" className="font-strong text-link">
              Audit log
            </AppLink>
            {roleNote ? (
              <ReadOnlyNote>Read-only for your role · Operators can quarantine and release routes</ReadOnlyNote>
            ) : null}
          </>
        }
      />
      {query.data?.issues.map((issue) => (
        <Callout
          key={issue.id}
          tone="waiting"
          className="mx-4 mb-2 max-compact:mx-2"
          title={`${issue.platform}: ${issue.label}`}
        >
          <span className="block">
            {issue.evidence ? `${issue.evidence} · ` : ""}since {formatDateTime(issue.opened_at)}
            {issue.expected_reset ? ` · expected reset ${formatDateTime(issue.expected_reset)}` : ""}
          </span>
          <span className="block">Next: {issue.next_step}</span>
        </Callout>
      ))}
      <Toolbar>
        <SegmentedFilter
          label="Filter by kind"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All", count: routes.length },
            {
              value: "acquisition",
              label: "Acquisition",
              count: routes.filter((r) => r.category === "acquisition").length,
            },
            {
              value: "processing",
              label: "Processing",
              count: routes.filter((r) => r.category === "processing").length,
            },
          ]}
        />
        <span className="text-ui text-muted">
          {held > 0 ? `${String(held)} quarantined or stopped` : "None quarantined"}
        </span>
        <ToolbarSpacer />
      </Toolbar>
      {query.isError ? (
        <Callout tone="attention" role="alert" className="mx-4 mb-2">
          Couldn&apos;t load the routes. {errorText(query.error)}{" "}
          <LinkButton
            onClick={() => {
              void query.refetch();
            }}
          >
            Retry
          </LinkButton>
        </Callout>
      ) : null}
      <GridWrap tabIndex={0} role="region" aria-label="Routes table" className="flex-none rounded-b-xl border-b">
        <DataTable
          wrap={false}
          label="Routes"
          columns={columns}
          rows={rows}
          rowId={(r) => r.id}
          minWidth={1300}
          pinned={PINNED}
          isPending={query.isPending}
          skeletonRows={4}
          empty={query.data ? "No routes configured" : undefined}
          rowOptions={(r) => ({
            titles: {
              ...(r.credential ? { credential: r.credential.name } : {}),
              ...(r.quota === null ? {} : { quota: r.quota }),
              ...(r.last_failure ? { last_failure: r.last_failure.detail } : {}),
            },
          })}
        />
      </GridWrap>
      <DependenciesPanel onRecorded={() => void query.refetch()} />
      {quarantining ? (
        <QuarantineDialog
          route={quarantining}
          onClose={() => {
            setQuarantining(null);
          }}
        />
      ) : null}
      {releasing ? (
        <ReleaseDialog
          route={releasing}
          onClose={() => {
            setReleasing(null);
          }}
        />
      ) : null}
    </section>
  );
}
